<lib-page-header
    [title]="selectedTask ? 'Schedule ' + selectedTask.taskType : 'Schedule'"
    screenName="Scheduling">
  <mat-icon icon>schedule</mat-icon>
  <ng-container subtitle>
    <span *ngIf="!selectedTask && !selectedTaskError">Define a schedule and preview the next executions on the Gantt. Simulation tab feeds bar widths.</span>
    <span *ngIf="selectedTask" class="task-handoff-subtitle">
      <mat-icon class="task-handoff-icon">auto_awesome_mosaic</mat-icon>
      <strong>{{ selectedTask.name || selectedTask.taskType }}</strong>
      <span class="task-handoff-version">{{ selectedTask.definitionVersion }}</span>
      <span class="task-handoff-sep" *ngIf="selectedTask.description">·</span>
      <span class="task-handoff-desc" *ngIf="selectedTask.description">{{ selectedTask.description }}</span>
    </span>
    <span *ngIf="selectedTaskError" class="task-handoff-error">
      <mat-icon class="task-handoff-error-icon">error_outline</mat-icon>
      {{ selectedTaskError }}
    </span>
  </ng-container>
  <!-- Stats strip — only when preview is OPEN and we have events. Simulated-
       runtime and SIM chip removed (they were redundant: the configure-
       preview icon-button already reaches the dialog where sim values live,
       and the chip duplicated info that's already on the preview itself). -->
  <ng-container stats *ngIf="previewOpen && events.length > 0">
    <div class="page-header-stat">
        <mat-icon class="stat-icon-events">event_note</mat-icon>
        <span class="stat-value">{{ events.length }}</span>
        <span class="stat-label">Events</span>
    </div>
    <div class="page-header-stat">
        <mat-icon class="stat-icon-events">play_circle</mat-icon>
        <span class="stat-value">{{ executionCount() }}</span>
        <span class="stat-label">Executions</span>
    </div>
  </ng-container>
  <ng-container actions>
    <!-- All actions are icon-buttons in a single uncluttered row. The
         preview toggle is the always-visible entry point; the rest only
         surface when the preview is open and operate on it. -->
    <button mat-icon-button
            class="actions-icon-btn"
            [class.active]="previewOpen"
            (click)="togglePreview()"
            [matTooltip]="previewOpen ? 'Hide preview' : 'Preview firings'"
            [attr.aria-label]="previewOpen ? 'Hide preview' : 'Preview firings'">
      <mat-icon>{{ previewOpen ? 'visibility_off' : 'visibility' }}</mat-icon>
    </button>

    <ng-container *ngIf="previewOpen">
      <mat-button-toggle-group [value]="viewMode" (change)="onViewModeChange($event.value)" hideSingleSelectionIndicator
                               aria-label="Preview view mode" class="view-toggle">
        <mat-button-toggle value="gantt" matTooltip="Gantt — bar-chart view, best for spotting overlaps">
          <mat-icon>view_timeline</mat-icon>
        </mat-button-toggle>
        <mat-button-toggle value="table" matTooltip="Table — exact times, drift, jitter, status">
          <mat-icon>table_rows</mat-icon>
        </mat-button-toggle>
      </mat-button-toggle-group>

      <button mat-icon-button class="actions-icon-btn"
              (click)="openPreviewDialog()"
              matTooltip="Configure preview (sample size, simulation duration, jitter, failure, drift)"
              aria-label="Configure preview">
        <mat-icon>tune</mat-icon>
      </button>

      <button mat-icon-button class="actions-icon-btn"
              (click)="toggleFullscreen()"
              [matTooltip]="previewFullscreen ? 'Exit fullscreen' : 'Expand to full screen'"
              [attr.aria-label]="previewFullscreen ? 'Exit fullscreen' : 'Expand to full screen'">
        <mat-icon>{{ previewFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</mat-icon>
      </button>

      <!-- Gantt → PNG export.  Table → browser print. Same icon-button
           shape so the row reads as one consistent action cluster. -->
      <button mat-icon-button class="actions-icon-btn"
              *ngIf="viewMode === 'gantt'"
              (click)="downloadGantt()"
              matTooltip="Export the Gantt as a PNG" aria-label="Export Gantt as PNG">
        <mat-icon>file_download</mat-icon>
      </button>
      <button mat-icon-button class="actions-icon-btn"
              *ngIf="viewMode === 'table'"
              (click)="printTable()"
              matTooltip="Print the events table" aria-label="Print events table">
        <mat-icon>print</mat-icon>
      </button>
    </ng-container>
  </ng-container>
</lib-page-header>

<!-- Live schedule literal — sits right under the page-header so the operator
     sees the plain-English summary the moment they open the page, instead of
     having to scroll past the form tabs to find the description box. -->
<div class="schedule-literal" *ngIf="description">
  <mat-icon class="literal-icon">description</mat-icon>
  <span class="literal-text">{{ description }}</span>
</div>

<!-- Capability picker — direct-open path (no Task Catalog handoff). Pick the
     capability (taskType + version) to schedule; its descriptor renders below. -->
<div class="capability-pick-bar" *ngIf="!selectedTask">
  <mat-form-field appearance="outline" class="capability-pick-field" subscriptSizing="dynamic">
    <mat-label>Capability to schedule</mat-label>
    <mat-select [value]="selectedCapability" [compareWith]="compareCapabilities"
                (selectionChange)="onCapabilityPicked($event.value)">
      @for (c of capabilities; track c.code + ':' + c.version) {
        <mat-option [value]="c">{{ c.code }} · v{{ c.version }}</mat-option>
      }
    </mat-select>
    <mat-hint *ngIf="!capabilities.length">No ACTIVE capabilities registered.</mat-hint>
  </mat-form-field>
</div>

<!-- Task Inputs — the capability descriptor rendered as a form (TASK-221).
     Connector fields become live connection pickers (nucleus-connectors-service);
     userdata fields the UserData picker. The captured payload travels with the
     schedule at submit; the scheduler maps Connector fields →
     ExecuteCommand.bindings (fieldName → chosen connection id) at dispatch. -->
<div class="task-inputs-section" *ngIf="taskInputSchema">
  <div class="task-inputs-header">
    <mat-icon>tune</mat-icon>
    <span>Task Inputs</span>
  </div>
  <app-schema-form
      [schema]="taskInputSchema"
      [value]="taskPayload"
      (valueChange)="onPayloadChange($event)"
      (validityChange)="onInputsValidity($event)" />
</div>

<!-- Save bar — name the schedule + persist it as a Job (TASK-221 SAVE). The
     scheduler maps the payload's Connector fields → ExecuteCommand.bindings at
     dispatch (S3). Shown once a task is selected. -->
<div class="job-save-bar" *ngIf="selectedCapability">
  <mat-form-field appearance="outline" class="job-name-field" subscriptSizing="dynamic">
    <mat-label>Schedule name</mat-label>
    <input matInput [formControl]="jobName" placeholder="e.g. Nightly NE poll" required />
    <mat-error *ngIf="jobName.hasError('required')">A name is required</mat-error>
  </mat-form-field>
  <span class="job-save-spacer"></span>
  <button mat-flat-button color="primary" [disabled]="!canSave()" (click)="submitForm()">
    <mat-icon>{{ saving ? 'hourglass_empty' : 'save' }}</mat-icon>
    {{ saving ? 'Saving…' : 'Save schedule' }}
  </button>
</div>

<mat-drawer-container class="example-container"
                      [class.preview-open]="previewOpen"
                      [class.preview-fullscreen]="previewFullscreen">
  <mat-drawer mode="side" opened>
    <mat-card>
      <mat-card-content>
        <!-- Dual-layout: tab-group when preview is open, 3-column spread when
             preview is closed. Each section's content lives in an ng-template
             so it's defined once and rendered into whichever layout is active.
             Trade-off: switching layouts destroys / recreates the form
             component instances, so unsaved Hourly / Daily / etc. fields
             reset on toggle. Acceptable for this prototype page; if we keep
             this pattern, plumb a parent-level snapshot to restore state. -->
        <mat-tab-group *ngIf="previewOpen" mat-align-tabs="start">
          <mat-tab label="Schedule">
            <ng-container *ngTemplateOutlet="scheduleSection"></ng-container>
          </mat-tab>
          <mat-tab label="Execution" *ngIf="isRecurring()">
            <ng-container *ngTemplateOutlet="executionSection"></ng-container>
          </mat-tab>
          <mat-tab label="Time Ranges">
            <ng-container *ngTemplateOutlet="timeRangesSection"></ng-container>
          </mat-tab>
        </mat-tab-group>

        <div *ngIf="!previewOpen" class="three-column-form">
          <div class="form-column">
            <div class="section-title">Schedule</div>
            <ng-container *ngTemplateOutlet="scheduleSection"></ng-container>
          </div>
          <div class="form-column" *ngIf="isRecurring()">
            <div class="section-title">Execution</div>
            <ng-container *ngTemplateOutlet="executionSection"></ng-container>
          </div>
          <div class="form-column">
            <div class="section-title">Time Ranges</div>
            <ng-container *ngTemplateOutlet="timeRangesSection"></ng-container>
          </div>
        </div>

        <!-- ── Section templates — single source of truth for each form group -->
        <ng-template #scheduleSection>
          <div class="tab-container one-column-container">
            <!-- Frequency — button-toggle group instead of a dropdown. All
                 7 options (Once / Hourly / Daily / Weekly / Monthly / Yearly /
                 Cron) visible at once with icons; flex-wraps on narrow
                 columns. Replaces the click-and-scroll mat-select. -->
            <div class="freq-toggle-wrap">
              <span class="freq-toggle-label">Frequency</span>
              <mat-button-toggle-group [formControl]="frequencyControl"
                                       hideSingleSelectionIndicator
                                       class="freq-toggle">
                <mat-button-toggle *ngFor="let frequency of frequencies" [value]="frequency"
                                   [matTooltip]="frequency | titlecase">
                  <mat-icon class="freq-toggle-icon">{{ getFrequencyIcon(frequency) }}</mat-icon>
                  <span class="freq-toggle-text">{{ frequency | titlecase }}</span>
                </mat-button-toggle>
              </mat-button-toggle-group>
            </div>

            <div class="two-column-container">
              <mat-form-field appearance="outline">
                <mat-label>Start Date</mat-label>
                <input matInput [matDatepicker]="startPicker" [formControl]="startDate" />
                <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
                <mat-datepicker #startPicker></mat-datepicker>
                <mat-hint align="start">MMMM DD,YYYY</mat-hint>
                <mat-hint align="end" *ngIf="startDate.value">
                  <a class="date-clear-link"
                     (click)="$event.stopPropagation(); startDate.setValue(null)">Clear</a>
                </mat-hint>
                <mat-error *ngIf="isStartDateError()">{{startDateErrorMessage()}}</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" *ngIf="isRecurring()">
                <mat-label>End Date</mat-label>
                <input matInput [matDatepicker]="endPicker" [formControl]="endDate" />
                <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
                <mat-datepicker #endPicker></mat-datepicker>
                <mat-hint align="start">MMMM DD,YYYY</mat-hint>
                <mat-hint align="end" *ngIf="endDate.value">
                  <a class="date-clear-link"
                     (click)="$event.stopPropagation(); endDate.setValue(null)">Clear</a>
                </mat-hint>
                <mat-error *ngIf="isEndDateError()">{{endDateErrorMessage()}}</mat-error>
              </mat-form-field>
            </div>

            <mat-form-field appearance="outline">
              <mat-label>Timezone</mat-label>
              <mat-icon matIconPrefix>public</mat-icon>
              <mat-select [formControl]="timeZone">
                <mat-option *ngFor="let tz of availableTimeZones" [value]="tz">{{ tz }}</mat-option>
              </mat-select>
              <mat-hint>All time-of-day fields below interpret in this zone.</mat-hint>
            </mat-form-field>
          </div>
        </ng-template>

        <ng-template #executionSection>
          <div class="tab-container one-column-container">
            <hourly-schedule-options #SCHEDULER_OPTIONS *ngIf="isRecurringHourly()" [valueChange]="refreshGantt.bind(this)"/>
            <daily-schedule-options #SCHEDULER_OPTIONS *ngIf="isRecurringDaily()" [valueChange]="refreshGantt.bind(this)"/>
            <weekly-schedule-options #SCHEDULER_OPTIONS *ngIf="isRecurringWeekly()" [valueChange]="refreshGantt.bind(this)"/>
            <monthly-schedule-options #SCHEDULER_OPTIONS *ngIf="isRecurringMonthly()" [valueChange]="refreshGantt.bind(this)"/>
            <yearly-schedule-options #SCHEDULER_OPTIONS *ngIf="isRecurringYearly()" [valueChange]="refreshGantt.bind(this)"/>
            <cron-scheduler-options #SCHEDULER_OPTIONS *ngIf="isRecurringCron()" [valueChange]="refreshGantt.bind(this)"/>
          </div>
        </ng-template>

        <ng-template #timeRangesSection>
          <time-range-selector #TIME_RANGES_SELECTOR [timeRangeForm]="schedulerForm"></time-range-selector>
        </ng-template>

        <!-- Old description-message-container removed — the live literal now
             lives in the prominent banner under the page-header. Service
             errors now surface via BaseComponent.showError() (ngx-toastr) —
             same global-error pattern goldfish uses, no inline banner. -->
      </mat-card-content>
    </mat-card>
  </mat-drawer>
  <mat-drawer-content>
    <!-- Preview is on-demand. Hidden by default; appears when previewOpen
         is true (toggle in the page-header). In fullscreen mode the pane
         floats over the viewport with its own panel-shell header
         (description + collapse button), so it reads as a proper window
         rather than an unmarked overlay. -->
    <div class="preview-pane" *ngIf="previewOpen">
      <!-- Fullscreen header — uses the canonical lib-page-header (same
           pattern as gantt-event-view, audit-detail-view, the preview-options
           dialog). All actions for the preview live here when fullscreen,
           so the operator never needs to exit fullscreen to switch view /
           configure / export / print. -->
      <lib-page-header *ngIf="previewFullscreen"
                       screenName="Preview · Fullscreen"
                       [title]="viewMode === 'gantt' ? 'Gantt — next firings' : 'Table — next firings'"
                       backTo="">
        <mat-icon icon>{{ viewMode === 'gantt' ? 'view_timeline' : 'table_rows' }}</mat-icon>
        <ng-container subtitle *ngIf="description">
          <span>{{ description }}</span>
        </ng-container>
        <ng-container actions>
          <mat-button-toggle-group [value]="viewMode" (change)="onViewModeChange($event.value)"
                                   hideSingleSelectionIndicator
                                   aria-label="Preview view mode" class="view-toggle">
            <mat-button-toggle value="gantt" matTooltip="Gantt — bar-chart view">
              <mat-icon>view_timeline</mat-icon>
            </mat-button-toggle>
            <mat-button-toggle value="table" matTooltip="Table — exact times, drift, jitter, status">
              <mat-icon>table_rows</mat-icon>
            </mat-button-toggle>
          </mat-button-toggle-group>

          <button mat-icon-button class="actions-icon-btn"
                  (click)="openPreviewDialog()"
                  matTooltip="Configure preview" aria-label="Configure preview">
            <mat-icon>tune</mat-icon>
          </button>

          <button mat-icon-button class="actions-icon-btn"
                  *ngIf="viewMode === 'gantt'"
                  (click)="downloadGantt()"
                  matTooltip="Export the Gantt as a PNG" aria-label="Export Gantt as PNG">
            <mat-icon>file_download</mat-icon>
          </button>
          <button mat-icon-button class="actions-icon-btn"
                  *ngIf="viewMode === 'table'"
                  (click)="printTable()"
                  matTooltip="Print the events table" aria-label="Print events table">
            <mat-icon>print</mat-icon>
          </button>

          <button mat-icon-button class="actions-icon-btn"
                  (click)="toggleFullscreen()"
                  matTooltip="Collapse fullscreen" aria-label="Collapse fullscreen">
            <mat-icon>fullscreen_exit</mat-icon>
          </button>
          <button mat-icon-button class="actions-icon-btn"
                  (click)="togglePreview()"
                  matTooltip="Close preview" aria-label="Close preview">
            <mat-icon>close</mat-icon>
          </button>
        </ng-container>
      </lib-page-header>

      <div class="preview-content" id="schedule-printable-preview">
        <scheduler-events-gantt #SCHEDULER_EVENTS_GANTT
                                *ngIf="viewMode === 'gantt'"
                                [events]="events"
                                [viewType]="viewType"
                                [eventDependency]="eventDependency"
                                [simulationDurationMs]="simulation.durationMs"
                                [simulationJitterPercent]="simulation.jitterPercent"
                                [simulationFailurePercent]="simulation.failurePercent"
                                [simulationDriftMs]="simulation.driftMs">
        </scheduler-events-gantt>

        <scheduler-events-table *ngIf="viewMode === 'table'"
                                [events]="events"
                                [simulationParams]="simulation">
        </scheduler-events-table>
      </div>
    </div>

    <!-- When preview is closed, the drawer-content stays empty so the form
         drawer expands to fill the available width (CSS handles the layout
         shift). Operators see a clean form-only layout. -->
  </mat-drawer-content>
</mat-drawer-container>

<!-- Preview-options dialog. Same bw-dialog shell + <lib-page-header> shape as
     gantt-event-view / audit-detail-view (the canonical pattern — never use a
     hand-rolled .bw-header div anymore). The simulation-options component
     still emits (paramsChange) live, so dialing while the dialog is open
     re-renders the Gantt behind it. -->
<ng-template #previewDialog>
  <div class="bw-dialog preview-options-dialog">
    <lib-page-header screenName="Preview" title="Preview options" backTo="">
      <mat-icon icon>tune</mat-icon>
      <ng-container subtitle>
        <span>Local-only — these knobs feed the Gantt preview, not the saved schedule.</span>
      </ng-container>
      <ng-container actions>
        <button mat-icon-button (click)="closePreviewDialog()" matTooltip="Close" aria-label="Close">
          <mat-icon>close</mat-icon>
        </button>
      </ng-container>
    </lib-page-header>
    <div class="bw-body">
      <!-- [initial]="simulation" seeds the form with the LAST-SET values
           every time the dialog opens. [reactive]="false" stops the form
           from auto-emitting on each input — Apply commits, Cancel discards. -->
      <simulation-options #PREVIEW_SIM_OPTS
          [initial]="simulation"
          [reactive]="false"
          (paramsChange)="onSimulationChanged($event)">
      </simulation-options>
    </div>
    <footer class="bw-dialog-footer">
      <!-- Reset on the LEFT — restores the form fields to defaults but
           doesn't commit. User can then Cancel (discard) or Apply (commit
           the defaults). Icon-only because it's a secondary action. -->
      <button mat-icon-button type="button" (click)="PREVIEW_SIM_OPTS.reset()"
              matTooltip="Reset to defaults (no duration, no jitter, no failures, no drift)"
              aria-label="Reset preview settings to defaults">
        <mat-icon>restart_alt</mat-icon>
      </button>
      <span class="bw-dialog-footer-spacer"></span>
      <button mat-button type="button" (click)="closePreviewDialog()">Cancel</button>
      <button mat-flat-button color="primary" type="button"
              (click)="applyPreviewDialog(PREVIEW_SIM_OPTS)">Apply</button>
    </footer>
  </div>
</ng-template>







